import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, ActivityIndicator, Platform, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import { uiVar } from './floodlight/uiStyleCss';
import { ADM_DISPLAY } from './floodlight/vars/admin';

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
  || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
);

const ACCENT = '#a78bfa';
const ACCENT2 = '#818cf8';
const BG = '#0b0b0b';
const CARD = '#141418';
const CARD_BORDER = '#1e1e24';
const MUTED = 'rgba(255,255,255,0.4)';
const TEXT = '#e5e5e5';

const TIER_COLORS = {
  'Tier 1': '#8B5CF6',
  'Tier 2': '#3B82F6',
  'Tier 3': '#6B7280',
  'Tier 4 (DFS)': '#92400E',
  'Unknown': '#374151',
};

// ───────── Animated count-up ─────────

function useCountUp(target, duration = 1200) {
  const [value, setValue] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    const num = typeof target === 'number' ? target : parseInt(String(target).replace(/[^0-9]/g, ''), 10);
    if (!num || isNaN(num)) { setValue(target); return; }

    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(num * eased));
      if (progress < 1) frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [target, duration]);

  return typeof target === 'number' ? value : target;
}

function CountUpText({ value, style, suffix = '' }) {
  const animated = useCountUp(value);
  const display = typeof value === 'number'
    ? animated.toLocaleString() + suffix
    : String(value);
  return <Text style={style}>{display}</Text>;
}

// ───────── Progress Ring ─────────

function ProgressRing({ percent, size = 80, strokeWidth = 6, color = ACCENT, label }) {
  const pctNum = parseFloat(percent) || 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pctNum / 100);

  if (Platform.OS !== 'web') {
    return (
      <View style={{ alignItems: 'center', gap: 4 }}>
        <Text style={{ color, fontSize: 22, fontWeight: '800' }}>{percent}</Text>
        {label && <Text style={{ color: MUTED, fontSize: 10 }}>{label}</Text>}
      </View>
    );
  }

  return (
    <View style={{ alignItems: 'center', gap: 4 }}>
      <View style={{ width: size, height: size, position: 'relative' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} />
          <circle cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke={color} strokeWidth={strokeWidth}
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 1.2s ease-out' }} />
        </svg>
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 16, fontWeight: '800' }}>{percent}</Text>
        </View>
      </View>
      {label && <Text style={{ color: MUTED, fontSize: 10, textAlign: 'center' }}>{label}</Text>}
    </View>
  );
}

// ───────── Main Component ─────────

export default function MarketAuditTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [generateMsg, setGenerateMsg] = useState('');
  const [activeView, setActiveView] = useState('overview');
  const [marketSearch, setMarketSearch] = useState('');
  const [bookSearch, setBookSearch] = useState('');
  const [tierFilter, setTierFilter] = useState(null);
  const [expandedSport, setExpandedSport] = useState(null);
  const [expandedMarket, setExpandedMarket] = useState(null);
  const [expandedBook, setExpandedBook] = useState(null);
  const [marketSort, setMarketSort] = useState('sportsbooks');
  const [bookSort, setBookSort] = useState('markets');

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const base = getBase();
      if (!base) throw new Error('Functions base URL not configured');
      const res = await fetch(`${base}/getMarketAuditSnapshot`);
      const json = await res.json();
      if (json.success) {
        setData(json);
      } else if (json.empty) {
        setData(null);
      } else {
        throw new Error(json.error || 'Failed to fetch snapshot');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenerateMsg('');
    try {
      const base = getBase();
      const res = await fetch(`${base}/generateMarketAuditReport`, { method: 'POST' });
      const json = await res.json();
      if (json.success) {
        setGenerateMsg(`Report generated: ${json.markets} markets, ${json.sportsbooks} sportsbooks (${json.scan_duration_ms}ms)`);
        fetchSnapshot();
      } else {
        setGenerateMsg(`Error: ${json.error}`);
      }
    } catch (e) {
      setGenerateMsg(`Error: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  }, [fetchSnapshot]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const base = getBase();
      const res = await fetch(`${base}/getMarketAuditDownload`);
      const json = await res.json();
      if (json.success && json.url) {
        if (Platform.OS === 'web') {
          window.open(json.url, '_blank');
        }
      } else {
        setGenerateMsg(json.error || 'No report available');
      }
    } catch (e) {
      setGenerateMsg(`Download error: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleExportJSON = useCallback(() => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `market-audit-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);

  const filteredMarkets = useMemo(() => {
    if (!data?.markets) return [];
    let list = data.markets;
    if (marketSearch) {
      const q = marketSearch.toLowerCase();
      list = list.filter(m => m.market.toLowerCase().includes(q));
    }
    if (marketSort === 'sportsbooks') list = [...list].sort((a, b) => b.sportsbook_count - a.sportsbook_count);
    else if (marketSort === 'fixtures') list = [...list].sort((a, b) => b.fixture_count - a.fixture_count);
    else if (marketSort === 'name') list = [...list].sort((a, b) => a.market.localeCompare(b.market));
    return list;
  }, [data, marketSearch, marketSort]);

  const filteredBooks = useMemo(() => {
    if (!data?.sportsbooks) return [];
    let list = data.sportsbooks;
    if (bookSearch) {
      const q = bookSearch.toLowerCase();
      list = list.filter(b => b.sportsbook.toLowerCase().includes(q));
    }
    if (tierFilter) list = list.filter(b => b.tier === tierFilter);
    if (bookSort === 'markets') list = [...list].sort((a, b) => b.market_count - a.market_count);
    else if (bookSort === 'name') list = [...list].sort((a, b) => a.sportsbook.localeCompare(b.sportsbook));
    return list;
  }, [data, bookSearch, tierFilter, bookSort]);

  // ─── Loading state ───
  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator size="large" color={ACCENT} />
        <Text style={s.loadingText}>Loading audit snapshot...</Text>
      </View>
    );
  }

  // ─── Error state ───
  if (error) {
    return (
      <View style={s.center}>
        <Ionicons name="alert-circle" size={48} color="#ef4444" />
        <Text style={s.errorText}>{error}</Text>
        <TouchableOpacity style={s.primaryBtn} onPress={fetchSnapshot}>
          <Text style={s.primaryBtnText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // ─── Empty state: no snapshot generated yet ───
  if (!data) {
    return (
      <View style={s.center}>
        <Ionicons name="bar-chart-outline" size={64} color={uiVar('adm-accent', ACCENT)} />
        <Text style={s.loadingText}>No Audit Snapshot Yet</Text>
        <Text style={s.mutedText}>Generate your first comprehensive market audit report.</Text>
        <TouchableOpacity style={s.primaryBtn} onPress={handleGenerate} disabled={generating}>
          {generating
            ? <ActivityIndicator size="small" color="#fff" />
            : <><Ionicons name="flash" size={18} color={uiVar('adm-ink', '#fff')} /><Text style={s.primaryBtnText}>Generate Report</Text></>
          }
        </TouchableOpacity>
        {generateMsg ? <Text style={s.genMsg}>{generateMsg}</Text> : null}
      </View>
    );
  }

  const km = data.key_metrics;
  const tb = data.tier_breakdown;

  return (
    <ScrollView style={s.wrap} contentContainerStyle={s.wrapInner}>

      {/* ═══ HERO BANNER ═══ */}
      <View style={s.heroBanner}>
        <View style={s.heroGlow} />
        <Text style={s.heroTitle}>Data Coverage Audit</Text>
        <Text style={s.heroSub}>
          {data.from_cache
            ? `Last scanned ${formatAge(data.cache_age_seconds)} ago`
            : `Fresh scan (${data.scan_duration_ms}ms)`
          } | Updated hourly
        </Text>

        <View style={s.heroNumbers}>
          <HeroStat value={km.total_active_markets_in_cache} label="Active Markets" icon="grid-outline" />
          <HeroStat value={km.total_active_sportsbooks_in_cache} label="Sportsbooks" icon="business-outline" />
          <HeroStat value={km.total_fixtures_cached} label="Live Fixtures" icon="football-outline" />
          <HeroStat value={km.total_odds_market_documents} label="Odds Documents" icon="documents-outline" />
          <HeroStat value={km.total_leagues_active} label="Active Leagues" icon="globe-outline" />
          <HeroStat value={km.player_props_players_cached} label="Players Tracked" icon="person-outline" />
        </View>
      </View>

      {/* ═══ COVERAGE GAUGES ═══ */}
      <View style={s.gaugeRow}>
        <View style={s.gaugeCard}>
          <ProgressRing percent={km.market_coverage_pct} color="#8B5CF6" label="Market Coverage" />
          <Text style={s.gaugeSub}>{km.total_active_markets_in_cache} of {km.total_configured_markets} configured</Text>
        </View>
        <View style={s.gaugeCard}>
          <ProgressRing percent={km.sportsbook_coverage_pct} color="#3B82F6" label="Sportsbook Coverage" />
          <Text style={s.gaugeSub}>{km.total_active_sportsbooks_in_cache} of {km.total_configured_sportsbooks} configured</Text>
        </View>
        <View style={s.gaugeCard}>
          <ProgressRing
            percent={km.total_fixtures_cached > 0
              ? ((km.fixtures_with_odds / km.total_fixtures_cached) * 100).toFixed(1) + '%'
              : '0%'}
            color="#10B981"
            label="Fixtures with Odds"
          />
          <Text style={s.gaugeSub}>{km.fixtures_with_odds} of {km.total_fixtures_cached} fixtures</Text>
        </View>
        <View style={s.gaugeCard}>
          <ProgressRing
            percent={km.player_props_unique_markets > 0
              ? Math.min(100, km.player_props_unique_markets).toFixed(0) + '+'
              : '0'}
            color="#F97316"
            label="Prop Market Types"
          />
          <Text style={s.gaugeSub}>{km.player_props_unique_markets} unique prop markets</Text>
        </View>
      </View>

      {/* ═══ SPORTSBOOK TIER CARDS ═══ */}
      <View style={s.tierCards}>
        {Object.entries(tb).map(([key, info]) => {
          const tierKey = key === 'tier1' ? 'Tier 1' : key === 'tier2' ? 'Tier 2' : key === 'tier3' ? 'Tier 3' : 'Tier 4 (DFS)';
          const color = TIER_COLORS[tierKey];
          const pct = info.count > 0 ? Math.round((info.active / info.count) * 100) : 0;
          return (
            <View key={key} style={[s.tierCard, { borderLeftColor: color, borderLeftWidth: 3 }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <View style={[s.tierDot, { backgroundColor: color }]} />
                <Text style={s.tierCardLabel}>{info.label}</Text>
              </View>
              <Text style={s.tierCardValue}>{info.active} <Text style={s.tierCardMuted}>/ {info.count}</Text></Text>
              <View style={s.tierBar}>
                <View style={[s.tierBarFill, { width: `${pct}%`, backgroundColor: color }]} />
              </View>
            </View>
          );
        })}
      </View>

      {/* ═══ TAB NAV ═══ */}
      <View style={s.tabRow}>
        {[
          { key: 'overview', label: 'Overview', icon: 'stats-chart-outline' },
          { key: 'sports', label: 'By Sport', icon: 'trophy-outline' },
          { key: 'markets', label: `Markets (${filteredMarkets.length})`, icon: 'grid-outline' },
          { key: 'sportsbooks', label: `Sportsbooks (${filteredBooks.length})`, icon: 'business-outline' },
          { key: 'playerprops', label: 'Player Props', icon: 'person-outline' },
          { key: 'download', label: 'Download', icon: 'download-outline' },
        ].map(t => (
          <TouchableOpacity key={t.key}
            style={[s.tabBtn, activeView === t.key && s.tabBtnActive]}
            onPress={() => setActiveView(t.key)}>
            <Ionicons name={t.icon} size={14} color={activeView === t.key ? uiVar('adm-ink', '#fff') : MUTED} />
            <Text style={[s.tabBtnText, activeView === t.key && s.tabBtnTextActive]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* ═══ TAB CONTENT ═══ */}
      {activeView === 'overview' && <OverviewSection data={data} />}
      {activeView === 'sports' && <SportsSection data={data} expanded={expandedSport} setExpanded={setExpandedSport} />}
      {activeView === 'markets' && (
        <MarketsSection markets={filteredMarkets} search={marketSearch} setSearch={setMarketSearch}
          sort={marketSort} setSort={setMarketSort} expanded={expandedMarket} setExpanded={setExpandedMarket} />
      )}
      {activeView === 'sportsbooks' && (
        <SportsbooksSection books={filteredBooks} search={bookSearch} setSearch={setBookSearch}
          tierFilter={tierFilter} setTierFilter={setTierFilter} sort={bookSort} setSort={setBookSort}
          expanded={expandedBook} setExpanded={setExpandedBook} />
      )}
      {activeView === 'playerprops' && <PlayerPropsSection data={data} />}
      {activeView === 'download' && (
        <DownloadSection generating={generating} downloading={downloading}
          generateMsg={generateMsg} onGenerate={handleGenerate}
          onDownloadCSV={handleDownload} onDownloadJSON={handleExportJSON} />
      )}
    </ScrollView>
  );
}

// ───────── Hero Stat ─────────

function HeroStat({ value, label, icon }) {
  return (
    <View style={s.heroStat}>
      <Ionicons name={icon} size={18} color={uiVar('adm-accent', ACCENT)} style={{ marginBottom: 4 }} />
      <CountUpText value={value} style={s.heroStatVal} suffix="+" />
      <Text style={s.heroStatLabel}>{label}</Text>
    </View>
  );
}

// ───────── Overview ─────────

function OverviewSection({ data }) {
  const topMarkets = data.markets?.slice(0, 20) || [];
  const topBooks = data.sportsbooks?.slice(0, 20) || [];

  return (
    <View style={{ gap: 16 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        {/* Top Markets */}
        <View style={[s.card, { flex: 1, minWidth: 320 }]}>
          <Text style={s.cardTitle}>Top 20 Markets by Sportsbook Count</Text>
          {topMarkets.map((m, i) => (
            <View key={m.market} style={s.listRow}>
              <Text style={s.listRank}>{i + 1}</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.listName}>{m.market}</Text>
                <Text style={s.listMeta}>{m.leagues.length} league{m.leagues.length !== 1 ? 's' : ''} | {m.fixture_count} fixture{m.fixture_count !== 1 ? 's' : ''}</Text>
              </View>
              <View style={s.listBadge}><Text style={s.listBadgeText}>{m.sportsbook_count} books</Text></View>
            </View>
          ))}
        </View>

        {/* Top Sportsbooks */}
        <View style={[s.card, { flex: 1, minWidth: 320 }]}>
          <Text style={s.cardTitle}>Top 20 Sportsbooks by Market Count</Text>
          {topBooks.map((b, i) => (
            <View key={b.sportsbook} style={s.listRow}>
              <Text style={s.listRank}>{i + 1}</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.listName}>{formatBookName(b.sportsbook)}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <View style={[s.tierPill, { backgroundColor: (TIER_COLORS[b.tier] || '#374151') + '30' }]}>
                    <Text style={[s.tierPillText, { color: TIER_COLORS[b.tier] || uiVar('adm-muted', '#9CA3AF') }]}>{b.tier}</Text>
                  </View>
                  <Text style={s.listMeta}>{b.leagues.length} league{b.leagues.length !== 1 ? 's' : ''}</Text>
                </View>
              </View>
              <View style={[s.listBadge, { backgroundColor: '#3B82F615' }]}>
                <Text style={[s.listBadgeText, { color: '#3B82F6' }]}>{b.market_count} mkts</Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Configured sports */}
      {data.configured_sports?.length > 0 && (
        <View style={s.card}>
          <Text style={s.cardTitle}>Configured Markets per Sport (from mapping)</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {data.configured_sports.map(sp => (
              <View key={sp.sport} style={s.sportStatChip}>
                <Text style={s.sportStatVal}>{sp.configured_markets}</Text>
                <Text style={s.sportStatName}>{sp.sport}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

// ───────── Sports Drill-down ─────────

function SportsSection({ data, expanded, setExpanded }) {
  const sports = data.by_sport || [];
  const leagues = data.by_league || [];

  return (
    <View style={{ gap: 8 }}>
      {sports.map(sp => {
        const isOpen = expanded === sp.sport;
        const sportLeagues = leagues.filter(l => l.sport === sp.sport);
        return (
          <TouchableOpacity key={sp.sport} style={s.card} onPress={() => setExpanded(isOpen ? null : sp.sport)} activeOpacity={0.7}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={s.sportName}>{sp.sport}</Text>
                <Text style={s.sportMeta}>
                  {sp.fixtures} fixture{sp.fixtures !== 1 ? 's' : ''} | {sp.with_odds} with odds | {sp.leagues.length} league{sp.leagues.length !== 1 ? 's' : ''} | {sp.total_market_instances} market instances
                </Text>
              </View>
              <View style={s.sportBadge}><Text style={s.sportBadgeText}>{sp.leagues.length}</Text></View>
              <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={MUTED} />
            </View>

            {isOpen && sportLeagues.length > 0 && (
              <View style={s.expandedArea}>
                <View style={s.tableHeader}>
                  <Text style={[s.thText, { flex: 2 }]}>League</Text>
                  <Text style={[s.thText, { width: 70, textAlign: 'right' }]}>Fixtures</Text>
                  <Text style={[s.thText, { width: 70, textAlign: 'right' }]}>w/ Odds</Text>
                  <Text style={[s.thText, { width: 80, textAlign: 'right' }]}>Markets</Text>
                </View>
                {sportLeagues.map(l => (
                  <View key={l.league} style={s.tableRow}>
                    <Text style={[s.tdText, { flex: 2, color: TEXT, fontWeight: '600' }]}>{l.league.toUpperCase()}</Text>
                    <Text style={[s.tdText, { width: 70, textAlign: 'right' }]}>{l.fixtures}</Text>
                    <Text style={[s.tdText, { width: 70, textAlign: 'right', color: l.with_odds > 0 ? '#10B981' : '#EF4444' }]}>{l.with_odds}</Text>
                    <Text style={[s.tdText, { width: 80, textAlign: 'right' }]}>{l.total_market_instances}</Text>
                  </View>
                ))}
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ───────── Markets ─────────

function MarketsSection({ markets, search, setSearch, sort, setSort, expanded, setExpanded }) {
  return (
    <View style={{ gap: 10 }}>
      <View style={s.filterRow}>
        <View style={s.searchBox}>
          <Ionicons name="search" size={16} color={MUTED} />
          <TextInput style={s.searchInput} placeholder="Search markets..." placeholderTextColor={MUTED}
            value={search} onChangeText={setSearch} />
          {search ? <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={16} color={MUTED} /></TouchableOpacity> : null}
        </View>
        <View style={s.sortRow}>
          {[{ key: 'sportsbooks', label: 'Books' }, { key: 'fixtures', label: 'Fixtures' }, { key: 'name', label: 'A-Z' }].map(o => (
            <TouchableOpacity key={o.key} style={[s.sortBtn, sort === o.key && s.sortBtnActive]} onPress={() => setSort(o.key)}>
              <Text style={[s.sortBtnText, sort === o.key && s.sortBtnTextActive]}>{o.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <Text style={s.resultCount}>{markets.length} market{markets.length !== 1 ? 's' : ''}</Text>

      {markets.map(m => {
        const isOpen = expanded === m.market;
        return (
          <TouchableOpacity key={m.market} style={s.card} onPress={() => setExpanded(isOpen ? null : m.market)} activeOpacity={0.7}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={s.itemName}>{m.market}</Text>
                <Text style={s.itemMeta}>
                  {m.sportsbook_count} book{m.sportsbook_count !== 1 ? 's' : ''} | {m.fixture_count} fixture{m.fixture_count !== 1 ? 's' : ''} | {m.leagues.length} league{m.leagues.length !== 1 ? 's' : ''}
                </Text>
              </View>
              <View style={s.countBadge}><Text style={s.countBadgeText}>{m.sportsbook_count}</Text></View>
              <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={MUTED} />
            </View>

            {isOpen && (
              <View style={s.expandedArea}>
                {m.avg_books_per_fixture > 0 && (
                  <Text style={s.itemMeta}>Avg {m.avg_books_per_fixture} books/fixture | Max {m.sportsbook_count} on a single fixture</Text>
                )}
                <Text style={s.expandedLabel}>Leagues:</Text>
                <View style={s.chipWrap}>
                  {m.leagues.map(l => <View key={l} style={s.chip}><Text style={s.chipText}>{l}</Text></View>)}
                </View>
                {m.sportsbooks_sample?.length > 0 && (
                  <>
                    <Text style={[s.expandedLabel, { marginTop: 8 }]}>Sportsbooks (sample):</Text>
                    <View style={s.chipWrap}>
                      {m.sportsbooks_sample.map(b => <View key={b} style={s.chipBook}><Text style={s.chipBookText}>{formatBookName(b)}</Text></View>)}
                    </View>
                  </>
                )}
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ───────── Sportsbooks ─────────

function SportsbooksSection({ books, search, setSearch, tierFilter, setTierFilter, sort, setSort, expanded, setExpanded }) {
  return (
    <View style={{ gap: 10 }}>
      <View style={s.filterRow}>
        <View style={s.searchBox}>
          <Ionicons name="search" size={16} color={MUTED} />
          <TextInput style={s.searchInput} placeholder="Search sportsbooks..." placeholderTextColor={MUTED}
            value={search} onChangeText={setSearch} />
          {search ? <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={16} color={MUTED} /></TouchableOpacity> : null}
        </View>
        <View style={s.sortRow}>
          {[{ key: 'markets', label: 'Markets' }, { key: 'name', label: 'A-Z' }].map(o => (
            <TouchableOpacity key={o.key} style={[s.sortBtn, sort === o.key && s.sortBtnActive]} onPress={() => setSort(o.key)}>
              <Text style={[s.sortBtnText, sort === o.key && s.sortBtnTextActive]}>{o.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={s.tierFilterRow}>
        <TouchableOpacity style={[s.tierFilterPill, !tierFilter && s.tierFilterPillActive]} onPress={() => setTierFilter(null)}>
          <Text style={[s.tierFilterText, !tierFilter && s.tierFilterTextActive]}>All</Text>
        </TouchableOpacity>
        {['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4 (DFS)'].map(t => (
          <TouchableOpacity key={t}
            style={[s.tierFilterPill, tierFilter === t && { backgroundColor: TIER_COLORS[t] + '30', borderColor: TIER_COLORS[t] }]}
            onPress={() => setTierFilter(tierFilter === t ? null : t)}>
            <View style={[s.tierDotSmall, { backgroundColor: TIER_COLORS[t] }]} />
            <Text style={[s.tierFilterText, tierFilter === t && { color: TIER_COLORS[t] }]}>{t}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <Text style={s.resultCount}>{books.length} sportsbook{books.length !== 1 ? 's' : ''}</Text>

      {books.map(b => {
        const isOpen = expanded === b.sportsbook;
        return (
          <TouchableOpacity key={b.sportsbook} style={s.card} onPress={() => setExpanded(isOpen ? null : b.sportsbook)} activeOpacity={0.7}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text style={s.itemName}>{formatBookName(b.sportsbook)}</Text>
                  <View style={[s.tierPill, { backgroundColor: (TIER_COLORS[b.tier] || '#374151') + '30' }]}>
                    <Text style={[s.tierPillText, { color: TIER_COLORS[b.tier] || uiVar('adm-muted', '#9CA3AF') }]}>{b.tier}</Text>
                  </View>
                </View>
                <Text style={s.itemMeta}>{b.market_count} market{b.market_count !== 1 ? 's' : ''} | {b.leagues.length} league{b.leagues.length !== 1 ? 's' : ''}</Text>
              </View>
              <View style={[s.countBadge, { backgroundColor: '#3B82F620' }]}>
                <Text style={[s.countBadgeText, { color: '#3B82F6' }]}>{b.market_count}</Text>
              </View>
              <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} color={MUTED} />
            </View>

            {isOpen && (
              <View style={s.expandedArea}>
                <Text style={s.expandedLabel}>Leagues:</Text>
                <View style={s.chipWrap}>
                  {b.leagues.map(l => <View key={l} style={s.chip}><Text style={s.chipText}>{l}</Text></View>)}
                </View>
                <Text style={[s.expandedLabel, { marginTop: 8 }]}>Markets ({b.markets.length}):</Text>
                <View style={s.chipWrap}>
                  {b.markets.map(m => <View key={m} style={s.chipBook}><Text style={s.chipBookText}>{m}</Text></View>)}
                </View>
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ───────── Player Props ─────────

function PlayerPropsSection({ data }) {
  const pp = data.player_props;
  if (!pp) return <Text style={s.noData}>No player props data available</Text>;

  return (
    <View style={{ gap: 16 }}>
      <View style={s.card}>
        <Text style={s.cardTitle}>Player Props Summary</Text>
        <View style={s.ppRow}>
          <View style={s.ppStat}>
            <CountUpText value={pp.total_players} style={s.ppStatVal} />
            <Text style={s.ppStatLabel}>Players Cached</Text>
          </View>
          <View style={s.ppStat}>
            <CountUpText value={pp.unique_prop_markets?.length || 0} style={s.ppStatVal} />
            <Text style={s.ppStatLabel}>Prop Market Types</Text>
          </View>
          <View style={s.ppStat}>
            <CountUpText value={pp.leagues?.length || 0} style={s.ppStatVal} />
            <Text style={s.ppStatLabel}>Leagues</Text>
          </View>
        </View>
      </View>

      {pp.leagues?.length > 0 && (
        <View style={s.card}>
          <Text style={s.cardTitle}>Leagues with Player Props</Text>
          <View style={s.chipWrap}>
            {pp.leagues.map(l => <View key={l} style={s.chip}><Text style={s.chipText}>{l}</Text></View>)}
          </View>
        </View>
      )}

      {pp.unique_prop_markets?.length > 0 && (
        <View style={s.card}>
          <Text style={s.cardTitle}>All Prop Market Types ({pp.unique_prop_markets.length})</Text>
          <View style={s.chipWrap}>
            {pp.unique_prop_markets.map(m => <View key={m} style={s.chipBook}><Text style={s.chipBookText}>{m}</Text></View>)}
          </View>
        </View>
      )}
    </View>
  );
}

// ───────── Download ─────────

function DownloadSection({ generating, downloading, generateMsg, onGenerate, onDownloadCSV, onDownloadJSON }) {
  return (
    <View style={{ gap: 16 }}>
      <View style={s.card}>
        <Text style={s.cardTitle}>Generate & Download Reports</Text>
        <Text style={s.mutedText}>
          Generate a comprehensive audit report that scans every market and sportsbook in the cache.
          The report is saved as a CSV in Cloud Storage for download and sharing.
        </Text>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
          <TouchableOpacity style={s.primaryBtn} onPress={onGenerate} disabled={generating}>
            {generating
              ? <><ActivityIndicator size="small" color="#fff" /><Text style={s.primaryBtnText}>Generating...</Text></>
              : <><Ionicons name="flash" size={16} color={uiVar('adm-ink', '#fff')} /><Text style={s.primaryBtnText}>Generate Fresh Report</Text></>
            }
          </TouchableOpacity>

          <TouchableOpacity style={s.secondaryBtn} onPress={onDownloadCSV} disabled={downloading}>
            {downloading
              ? <ActivityIndicator size="small" color={ACCENT} />
              : <><Ionicons name="document-text-outline" size={16} color={uiVar('adm-accent', ACCENT)} /><Text style={s.secondaryBtnText}>Download CSV</Text></>
            }
          </TouchableOpacity>

          {Platform.OS === 'web' && (
            <TouchableOpacity style={s.secondaryBtn} onPress={onDownloadJSON}>
              <Ionicons name="code-slash-outline" size={16} color={uiVar('adm-accent', ACCENT)} />
              <Text style={s.secondaryBtnText}>Export JSON</Text>
            </TouchableOpacity>
          )}
        </View>

        {generateMsg ? <Text style={s.genMsg}>{generateMsg}</Text> : null}
      </View>

      <View style={s.card}>
        <Text style={s.cardTitle}>What the Report Contains</Text>
        <View style={{ gap: 8 }}>
          {[
            'Every unique market name found in the odds cache',
            'Fixture count and sportsbook count per market',
            'Average and max sportsbooks per fixture for each market',
            'League coverage per market',
            'Every sportsbook with its tier, market count, and league presence',
            'Player props coverage: players, leagues, and prop market types',
          ].map((item, i) => (
            <View key={i} style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
              <Ionicons name="checkmark-circle" size={16} color="#10B981" style={{ marginTop: 1 }} />
              <Text style={{ color: TEXT, fontSize: 13, flex: 1 }}>{item}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

// ───────── Helpers ─────────

function formatBookName(raw) {
  return raw.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
}

function formatAge(seconds) {
  if (!seconds) return 'unknown';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

// ───────── Styles ─────────

const s = {
  wrap: { flex: 1, backgroundColor: uiVar('adm-paper', BG) },
  wrapInner: { padding: 20, paddingBottom: 60 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40, gap: 12 },
  loadingText: { color: TEXT, fontSize: 16, fontWeight: '600' },
  mutedText: { color: MUTED, fontSize: 13, lineHeight: 20 },
  errorText: { color: '#ef4444', fontSize: 15, textAlign: 'center' },
  noData: { color: MUTED, fontSize: 14, textAlign: 'center', padding: 40 },
  genMsg: { color: '#10B981', fontSize: 12, marginTop: 8 },

  primaryBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: uiVar('adm-accent-fill', ACCENT), paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10 },
  primaryBtnText: { color: uiVar('adm-ink', '#fff'), fontSize: 14, fontWeight: '700' },
  secondaryBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'transparent', paddingHorizontal: 18, paddingVertical: 11, borderRadius: 10, borderWidth: 1, borderColor: ACCENT + '50' },
  secondaryBtnText: { color: uiVar('adm-accent', ACCENT), fontSize: 13, fontWeight: '600' },

  // Hero
  heroBanner: { backgroundColor: CARD, borderRadius: 16, borderWidth: 1, borderColor: CARD_BORDER, padding: 28, marginBottom: 16, overflow: 'hidden', position: 'relative' },
  heroGlow: { position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: 100, backgroundColor: ACCENT + '08' },
  heroTitle: { ...ADM_DISPLAY, fontSize: 26, fontWeight: '800', color: uiVar('adm-ink', '#fff'), marginBottom: 4 },
  heroSub: { color: MUTED, fontSize: 12, marginBottom: 20 },
  heroNumbers: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between' },
  heroStat: { alignItems: 'center', minWidth: 100 },
  heroStatVal: { fontSize: 28, fontWeight: '800', color: uiVar('adm-ink', '#fff') },
  heroStatLabel: { fontSize: 10, color: MUTED, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 },

  // Gauges
  gaugeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  gaugeCard: { backgroundColor: CARD, borderRadius: 12, borderWidth: 1, borderColor: CARD_BORDER, padding: 16, alignItems: 'center', flex: 1, minWidth: 150, gap: 8 },
  gaugeSub: { color: MUTED, fontSize: 10, textAlign: 'center' },

  // Tiers
  tierCards: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  tierCard: { backgroundColor: CARD, borderRadius: 10, borderWidth: 1, borderColor: CARD_BORDER, padding: 14, flex: 1, minWidth: 160 },
  tierDot: { width: 8, height: 8, borderRadius: 4 },
  tierCardLabel: { color: TEXT, fontSize: 12, fontWeight: '600' },
  tierCardValue: { fontSize: 22, fontWeight: '800', color: uiVar('adm-ink', '#fff') },
  tierCardMuted: { fontSize: 14, fontWeight: '400', color: MUTED },
  tierBar: { height: 4, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 2, marginTop: 8 },
  tierBarFill: { height: 4, borderRadius: 2 },

  // Tabs
  tabRow: { flexDirection: 'row', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
  tabBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, backgroundColor: uiVar('adm-card', '#1a1a22'), borderWidth: 1, borderColor: CARD_BORDER },
  tabBtnActive: { backgroundColor: ACCENT + '20', borderColor: uiVar('adm-accent', ACCENT) },
  tabBtnText: { color: MUTED, fontSize: 13, fontWeight: '500' },
  tabBtnTextActive: { color: uiVar('adm-ink', '#fff') },

  // Cards
  card: { backgroundColor: CARD, borderRadius: 12, borderWidth: 1, borderColor: CARD_BORDER, padding: 16, marginBottom: 8 },
  cardTitle: { fontSize: 15, fontWeight: '700', color: uiVar('adm-ink', '#fff'), marginBottom: 12 },

  // Lists
  listRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: CARD_BORDER + '80' },
  listRank: { color: MUTED, fontSize: 11, fontWeight: '700', width: 22, textAlign: 'right' },
  listName: { color: uiVar('adm-ink', '#fff'), fontSize: 13, fontWeight: '600' },
  listMeta: { color: MUTED, fontSize: 11, marginTop: 1 },
  listBadge: { backgroundColor: ACCENT + '15', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8 },
  listBadgeText: { color: uiVar('adm-accent', ACCENT), fontSize: 12, fontWeight: '600' },

  // Filter & Sort
  filterRow: { flexDirection: 'row', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  searchBox: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: uiVar('adm-card', '#1a1a22'), borderRadius: 8, borderWidth: 1, borderColor: CARD_BORDER, paddingHorizontal: 12, paddingVertical: 8, flex: 1, minWidth: 200 },
  searchInput: { flex: 1, color: TEXT, fontSize: 14, outlineStyle: 'none' },
  sortRow: { flexDirection: 'row', gap: 4 },
  sortBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, backgroundColor: uiVar('adm-card', '#1a1a22'), borderWidth: 1, borderColor: CARD_BORDER },
  sortBtnActive: { backgroundColor: ACCENT + '20', borderColor: uiVar('adm-accent', ACCENT) },
  sortBtnText: { color: MUTED, fontSize: 12, fontWeight: '500' },
  sortBtnTextActive: { color: uiVar('adm-ink', '#fff') },
  resultCount: { color: MUTED, fontSize: 12, marginBottom: 4 },

  // Tier filters
  tierFilterRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  tierFilterPill: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, backgroundColor: uiVar('adm-card', '#1a1a22'), borderWidth: 1, borderColor: CARD_BORDER },
  tierFilterPillActive: { backgroundColor: ACCENT + '20', borderColor: uiVar('adm-accent', ACCENT) },
  tierFilterText: { color: MUTED, fontSize: 12, fontWeight: '500' },
  tierFilterTextActive: { color: uiVar('adm-ink', '#fff') },
  tierDotSmall: { width: 7, height: 7, borderRadius: 4 },

  tierPill: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6 },
  tierPillText: { fontSize: 10, fontWeight: '700', textTransform: 'uppercase' },

  // Items (markets/books)
  itemName: { fontSize: 14, fontWeight: '600', color: uiVar('adm-ink', '#fff') },
  itemMeta: { color: MUTED, fontSize: 11, marginTop: 2 },
  countBadge: { backgroundColor: ACCENT + '20', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, minWidth: 40, alignItems: 'center' },
  countBadgeText: { color: uiVar('adm-accent', ACCENT), fontSize: 14, fontWeight: '700' },

  // Expanded
  expandedArea: { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: CARD_BORDER },
  expandedLabel: { color: TEXT, fontSize: 12, fontWeight: '600', marginBottom: 6 },
  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
  chip: { backgroundColor: uiVar('adm-card', '#1e1e28'), borderRadius: 6, paddingHorizontal: 10, paddingVertical: 4 },
  chipText: { color: TEXT, fontSize: 11, textTransform: 'uppercase', fontWeight: '500' },
  chipBook: { backgroundColor: uiVar('adm-card', '#1a1a22'), borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3, borderWidth: 1, borderColor: CARD_BORDER },
  chipBookText: { color: MUTED, fontSize: 10 },

  // Sports
  sportName: { fontSize: 15, fontWeight: '700', color: uiVar('adm-ink', '#fff'), textTransform: 'capitalize' },
  sportMeta: { color: MUTED, fontSize: 11, marginTop: 2 },
  sportBadge: { backgroundColor: '#10B98120', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, minWidth: 36, alignItems: 'center' },
  sportBadgeText: { color: '#10B981', fontSize: 14, fontWeight: '700' },

  sportStatChip: { backgroundColor: uiVar('adm-card', '#1a1a22'), borderRadius: 8, paddingHorizontal: 14, paddingVertical: 8, borderWidth: 1, borderColor: CARD_BORDER, alignItems: 'center' },
  sportStatVal: { color: uiVar('adm-ink', '#fff'), fontSize: 18, fontWeight: '700' },
  sportStatName: { color: MUTED, fontSize: 10, marginTop: 2 },

  // Table
  tableHeader: { flexDirection: 'row', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: CARD_BORDER, marginBottom: 4 },
  thText: { color: MUTED, fontSize: 10, fontWeight: '700', textTransform: 'uppercase' },
  tableRow: { flexDirection: 'row', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: CARD_BORDER + '50' },
  tdText: { color: MUTED, fontSize: 12 },

  // Player props
  ppRow: { flexDirection: 'row', gap: 16, flexWrap: 'wrap' },
  ppStat: { backgroundColor: uiVar('adm-card', '#1a1a22'), borderRadius: 10, paddingHorizontal: 20, paddingVertical: 14, borderWidth: 1, borderColor: CARD_BORDER, alignItems: 'center', flex: 1, minWidth: 120 },
  ppStatVal: { color: uiVar('adm-ink', '#fff'), fontSize: 28, fontWeight: '700' },
  ppStatLabel: { color: MUTED, fontSize: 12, marginTop: 4, fontWeight: '500' },
};
